import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Paper,
  Breadcrumbs,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  NavigateNext as NavigateNextIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { hasPermission } from '../../../utils/permissions';
import { useNotification } from '../../../components/Notification';
import { maskPhoneDisplay } from '../../../utils/validation';
import Table from '../../../components/Table';
import AssignUsersModal from './AssignUsersModal';
import {
  fetchInit,
  fetchApprovalAccounts,
  selectApprovalAccounts,
  selectApprovalAccountTypes,
  selectCurrentType,
  selectApprovalAccountsTotal,
  selectApprovalAccountsPage,
  selectApprovalAccountsPageSize,
  selectApprovalAccountsLoading,
  selectApprovalAccountsError,
  selectApprovalAccountsSuccessMessage,
  setCurrentType,
  setPage,
  setPageSize,
  deleteApprovalAccount,
  bulkDeleteApprovalAccounts,
  clearError,
  clearSuccessMessage,
} from './slice';

function ApprovalAccounts() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showNotification } = useNotification();

  const approvalAccounts = useSelector(selectApprovalAccounts);
  const approvalAccountTypes = useSelector(selectApprovalAccountTypes);
  const currentType = useSelector(selectCurrentType);
  const total = useSelector(selectApprovalAccountsTotal);
  const page = useSelector(selectApprovalAccountsPage);
  const pageSize = useSelector(selectApprovalAccountsPageSize);
  const loading = useSelector(selectApprovalAccountsLoading);
  const error = useSelector(selectApprovalAccountsError);
  const successMessage = useSelector(selectApprovalAccountsSuccessMessage);

  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);

  // Check permissions
  const canDeleteApprovalAccount = hasPermission('approvalaccount', 'deleteApprovalAccount');
  const canAssignUsers = hasPermission('approvalaccount', 'assignApprovalAccount');

  // Fetch init data on mount
  useEffect(() => {
    dispatch(fetchInit());
  }, [dispatch]);

  // Fetch approval accounts when type or pagination changes
  useEffect(() => {
    if (approvalAccountTypes.length > 0) {
      dispatch(fetchApprovalAccounts({ type: currentType, page, pageSize }));
    }
  }, [dispatch, currentType, page, pageSize, approvalAccountTypes.length]);

  // Show notifications
  useEffect(() => {
    if (error) {
      showNotification(t(error) || error, 'error');
      dispatch(clearError());
    }
  }, [error, t, showNotification, dispatch]);

  useEffect(() => {
    if (successMessage) {
      showNotification(successMessage, 'success');
      dispatch(clearSuccessMessage());
      // Refresh table after successful operation
      dispatch(fetchApprovalAccounts({ type: currentType, page, pageSize }));
      setSelectedIds([]);
    }
  }, [successMessage, showNotification, dispatch, currentType, page, pageSize]);

  const handleTabChange = (event, newValue) => {
    dispatch(setCurrentType(newValue));
    setSelectedIds([]);
  };

  const handleDelete = (row) => {
    setAccountToDelete(row);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (accountToDelete?.id) {
      dispatch(deleteApprovalAccount(accountToDelete.id));
    }
    setShowDeleteDialog(false);
    setAccountToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setAccountToDelete(null);
  };

  const handleBulkDelete = () => {
    setShowBulkDeleteDialog(true);
  };

  const handleConfirmBulkDelete = () => {
    dispatch(bulkDeleteApprovalAccounts(selectedIds));
    setShowBulkDeleteDialog(false);
  };

  const handleCancelBulkDelete = () => {
    setShowBulkDeleteDialog(false);
  };

  const handleAssignUsers = () => {
    setShowAssignModal(true);
  };

  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
  };

  // Get current approval type name for display
  const getCurrentTypeName = () => {
    const typeObj = approvalAccountTypes.find(type => type.id === currentType);
    return typeObj ? t(typeObj.name) : '';
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '-';
    }
  };

  // Table columns matching Users page + Approval Type
  const columns = [
    {
      key: 'username',
      label: t('approvalAccounts.table.username'),
    },
    {
      key: 'email',
      label: t('approvalAccounts.table.email'),
    },
    {
      key: 'phone',
      label: t('approvalAccounts.table.phone'),
      render: (value) => maskPhoneDisplay(value),
    },
    {
      key: 'role',
      label: t('approvalAccounts.table.role'),
    },
    {
      key: 'status',
      label: t('approvalAccounts.table.status'),
      render: (value) => {
        const isActive = value === true || value === 1 || value === '1';
        const hasStatus = value !== undefined && value !== null;

        return (
          <Box
            component="span"
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.875rem',
              fontWeight: 500,
              backgroundColor: hasStatus ? (isActive ? '#d4edda' : '#f8d7da') : '#e2e8f0',
              color: hasStatus ? (isActive ? '#155724' : '#721c24') : '#64748b',
            }}
          >
            {hasStatus
              ? (isActive ? t('approvalAccounts.status.active') : t('approvalAccounts.status.inactive'))
              : '-'
            }
          </Box>
        );
      },
    },
    {
      key: 'approvalType',
      label: t('approvalAccounts.table.approvalType'),
      render: () => getCurrentTypeName(),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header Section */}
      <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DescriptionIcon fontSize="small" />
            <Typography variant="body2">{t('approvalAccounts.breadcrumb.userManagement')}</Typography>
          </Box>
          <Typography variant="body2" color="text.primary">
            {t('approvalAccounts.breadcrumb.approvalAccounts')}
          </Typography>
        </Breadcrumbs>

        {/* Page Title and Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
            {t('approvalAccounts.title')}
          </Typography>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            {canAssignUsers && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<PersonAddIcon />}
                onClick={handleAssignUsers}
                id="assignUsers"
                name="assignUsers"
                data-testid="approvalAccounts-button-assignUsers"
              >
                {t('approvalAccounts.assignUsers')}
              </Button>
            )}
            {canDeleteApprovalAccount && (
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
                disabled={selectedIds.length === 0}
                id="deleteSelected"
                name="deleteSelected"
                data-testid="approvalAccounts-button-deleteSelected"
              >
                {t('approvalAccounts.deleteSelected')}
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Paper elevation={0} sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', mt: 2 }}>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs
            value={currentType}
            onChange={handleTabChange}
            aria-label="approval account types"
          >
            {approvalAccountTypes.map((type) => (
              <Tab
                key={type.id}
                label={t(type.name)}
                value={type.id}
                id={`tab-${type.id}`}
                data-testid={`approvalAccounts-tab-${type.id}`}
              />
            ))}
          </Tabs>
        </Box>

        {/* Table */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff' }}>
          <Table
            id="approvalAccounts-table"
            columns={columns}
            data={approvalAccounts}
            selectable={true}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            onDelete={canDeleteApprovalAccount ? handleDelete : null}
            loading={loading}
            emptyMessage={t('approvalAccounts.noData')}
            idField="id"
            pagination={true}
            page={page}
            pageSize={pageSize}
            totalItems={total}
            onPageChange={(newPage) => dispatch(setPage(newPage))}
            onPageSizeChange={(newSize) => dispatch(setPageSize(newSize))}
            pageSizeOptions={[5, 10, 20, 50, 100]}
          />
        </Box>
      </Paper>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('approvalAccounts.deleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('approvalAccounts.deleteDialog.message', {
              username: accountToDelete?.username || '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            id="cancelDelete"
            name="cancelDelete"
            data-testid="approvalAccounts-button-cancelDelete"
          >
            {t('approvalAccounts.deleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmDelete"
            name="confirmDelete"
            data-testid="approvalAccounts-button-confirmDelete"
          >
            {t('approvalAccounts.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Bulk Delete Dialog */}
      <Dialog
        open={showBulkDeleteDialog}
        onClose={handleCancelBulkDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('approvalAccounts.bulkDeleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('approvalAccounts.bulkDeleteDialog.message', {
              count: selectedIds.length,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelBulkDelete}
            variant="outlined"
            id="cancelBulkDelete"
            name="cancelBulkDelete"
            data-testid="approvalAccounts-button-cancelBulkDelete"
          >
            {t('approvalAccounts.bulkDeleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmBulkDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmBulkDelete"
            name="confirmBulkDelete"
            data-testid="approvalAccounts-button-confirmBulkDelete"
          >
            {t('approvalAccounts.bulkDeleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Users Modal */}
      <AssignUsersModal
        open={showAssignModal}
        onClose={handleCloseAssignModal}
        currentType={currentType}
      />
    </Box>
  );
}

export default ApprovalAccounts;
